import * as React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { apiGet, apiPost, logApiError } from "../api";
import Dump from "../components/dump";
import Spinner, { SpinnerColorsEnum } from "../components/spinner";
import { isFunction } from "../helpers/utils";
import { fetchAvailableDiscounts, resumeCheckout } from "../loaders";
import "../../styles/plugins/dayPicker.css";
import { ContextSession } from "./app";
import { formatPrice, formatPriceWithCurrency, getOfferAdminFee, getOfferPrice, getOfferRecurringPrice, isSubscription } from "./utils";
import ProductDescription from "../components/productDescription";
import { clsx } from 'clsx';
import { getPlanName } from "./planNameOverrides";
import { DiscountPill, discountForOffer, type Discount } from "../components/discount";

type Props = Readonly<{
	setState: Function,
	// A ?plan= deep link captured and stripped from the URL by App (null when none).
	preselectPlan?: string | null,
}>

function Products({setState, preselectPlan, ...otherProps}: Props) {
	const [showcaseProducts, setShowcaseProducts] = useState(null);
	const [discounts, setDiscounts] = useState<Discount[]>([]);
	const state = useContext(ContextSession);
	const [loader, setLoader] = useState(false);

	// Deep-link support: /{club}?plan=<id> pre-selects a plan, so the WordPress site can
	// link straight to a product. App captures and strips the param on load (and resets to a
	// clean session for it) and hands it down here. The id is matched against the offer's
	// PRODUCT id — the one identifier that is both unique per plan and stable all month. The
	// showcase product and the offer both change on the 1st/16th as Resamania swaps the
	// -15/+15 selling-window variant; the product underneath does not. (The showcase id is
	// also accepted, for a single variant.)
	const preselectPlanId = preselectPlan ?? null;
	// Guards a single auto-select per page load (a re-render before the step advances must
	// not fire a second POST /resa/offer).
	const autoSelected = useRef(false);
	// Set when a ?plan= id matches no product, to fall back to the plan list instead of
	// showing the resolving spinner forever.
	const [preselectFailed, setPreselectFailed] = useState(false);

	// Backstop: a committed checkout must never sit on plan selection — choosing a
	// different plan here would strand the mandate that was already created. It is
	// practically unreachable (the locked start-date screen blocks the way back),
	// but if any path lands here, bounce them back to their place in the checkout
	// rather than showing the plans.
	const committed = Boolean(state?.mandateId || state?.payableId);

	useEffect(() => {
		if (committed) {
			resumeCheckout(state.hash, setState);
		}
	}, [committed]);

	useEffect(() => {
		apiGet('/resa/products/' + state?.hash).then((response) => {
			if (response) {
				setShowcaseProducts(response.data);
			}
		});

		// Discounts the club is running, so we can badge the applicable plan cards.
		fetchAvailableDiscounts(state.hash).then((data) => setDiscounts(Array.isArray(data) ? data : []));
	}, [state.hash]);

	// Once the plans are loaded, honour a ?plan= deep link — but only on a fresh session
	// (no plan chosen yet). "Select a different option" returns here with the offer still
	// set (stepBack does not clear it), so that guard stops the link hijacking a deliberate
	// change of plan. An unknown id is ignored and the plan list is shown as normal.
	useEffect(() => {
		if (!preselectPlanId || autoSelected.current || state?.offerId || !Array.isArray(showcaseProducts)) {
			return;
		}

		const match = showcaseProducts.find((product) => {
			const productId = String(product?.offer?.product ?? '').split('/').pop();
			const showcaseId = String(product?.['@id'] ?? '').split('/').pop();
			return preselectPlanId === productId || preselectPlanId === showcaseId;
		});
		const offerIri = match?.offer?.['@id'];

		if (offerIri) {
			autoSelected.current = true;
			selectOffer(offerIri);
		} else {
			// Unknown id — stop waiting and show the normal plan list.
			setPreselectFailed(true);
		}
	}, [showcaseProducts, preselectPlanId, state?.offerId]);

	function selectOffer(offerId) {
		setLoader(true);

		apiPost('/resa/offer/' + state?.hash, {offerId: offerId}).then((response) => {
			if (isFunction(setState)) {
				setState(response.data);
			}
		}).catch(function (error) {
			setLoader(false);
			logApiError(error);
		});
	}

	// Redirecting out (see above) — don't flash the plan list.
	if (committed) {
		return <div className="text-center my-16"><Spinner/></div>;
	}

	// Resolving a ?plan= deep link on a fresh session: show a spinner rather than flashing
	// the full plan list for the moment it takes to load the plans and auto-select. Once a
	// plan is chosen the step advances away from here; an unknown id clears this and the
	// list is shown. A "select a different option" return (offer already set) is excluded,
	// so it still shows the list immediately.
	const resolvingPreselect = Boolean(preselectPlanId) && !state?.offerId && !preselectFailed;
	if (resolvingPreselect) {
		return <div className="text-center my-16"><Spinner/></div>;
	}

	return (<>
		<section className={clsx("max-w-screen-xl mx-auto text-center px-4 sm:px-8")}>
			<h1 className={clsx("mt-8 sm:mt-14 mb-3 text-white text-4xl")}>BECOME A MEMBER</h1>
		</section>

		<div className="w-full px-6 sm:px-8">
			<p className="text-center mb-8">
				We have a variety of membership plans, so you can find which works best for you!
			</p>
		</div>

		{showcaseProducts === null && <div className="max-w-screen-xl mx-auto text-center px-4 sm:px-8 my-32"><Spinner/></div>}

		<div className="flex flex-wrap justify-center w-full px-3 sm:px-4 max-w-screen-xl mx-auto">
			{/*
				A row is a showcase product: the offer carries the pricing, while the
				name, description and highlighted flag live on the row itself.
				offer.name is only the rate variant ("Standard (+15)") and is the same
				across plans, so it is not a usable title.
			*/}
			{showcaseProducts?.map((showcaseProduct, indexProduct) => {
				const offer = showcaseProduct.offer;

				// Every offer in the showcase is displayed, recurring or not. The
				// one-off products ("1 month paid upfront", "Pay as you go") are
				// legitimate and appear on the live checkout; pricing them from
				// billingSchedule instead of priceTI is what previously hid them.
				const price = getOfferPrice(offer);

				// ...but an offer with neither a usable priceTI nor a billing schedule
				// has no price to show, and a plan card without a price is not a plan
				// card. Skip it rather than inventing a figure. The sandbox catalogue
				// contains one of these ("Trial session"); Feel Fit's does not, which
				// is why it went unnoticed until the flow was run against sandbox.
				if (price === null) {
					return null;
				}

				const recurring = getOfferRecurringPrice(offer);
				const adminFee = getOfferAdminFee(offer);
				const discount = discountForOffer(discounts, isSubscription(offer));

				return (
					<div key={showcaseProduct['@id'] ?? "product" + indexProduct} className={"w-full md:w-1/2 xl:w-1/3 mb-8 text-center px-3 sm:px-4"}>
						{/*
							Half the card padding sits on the outer box and half on the inner
							content box, so the promo pill (a direct child of the outer box)
							hugs the top with the tighter spacing from the design, while the
							standard content keeps the full padding it always had.
						*/}
						<div className="bg-white text-black rounded-3xl overflow-hidden h-full p-3 sm:p-5 flex flex-col items-center gap-2">
							{discount && <DiscountPill discount={discount}/>}

							<div className="p-3 sm:p-5 flex flex-col items-center grow w-full">
								<h2 className="text-6xl mb-2">
									<span className="text-5xl">&pound;</span>{formatPrice(price, offer.priceCurrency)}
								</h2>

								<h4 className="uppercase text-xl font-black mb-6">{getPlanName(showcaseProduct)}</h4>

								<p className="mb-1">
									{recurring !== null && <>
										{offer.billingSchedule.interval === 'P1W' ? 'Weekly' : null}
										{offer.billingSchedule.interval === 'P1M' ? 'Monthly' : null}&nbsp;fee&nbsp;
										{formatPriceWithCurrency(recurring, offer.priceCurrency)}
									</>}
									{adminFee ? <>
										{recurring !== null && <br/>}
										{formatPriceWithCurrency(adminFee, offer.priceCurrency)} joining fee
									</> : null}
								</p>

								<ProductDescription
									description={showcaseProduct.description}
									className="text-sm text-left w-full mt-4 mb-0 [&>li]:mb-1"
								/>

								<div className="grow"/>

								<button
									className="mb-0 mt-6 flex items-center justify-center"
									disabled={loader}
									onClick={() => selectOffer(offer['@id'])}
								>
									{/* Only a subscription is a "plan"; a one-off product is just selected. */}
									{isSubscription(offer) ? 'CHOOSE PLAN' : 'SELECT'}
									{loader && <Spinner color={SpinnerColorsEnum.Black} className="!w-4 !h-4"/>}
								</button>
							</div>
						</div>
					</div>
				);
			})}
		</div>

		{/*<Dump value={showcaseProducts}/>*/}
	</>);
}

export default Products;
